/**
 * @fileoverview AudioStreamerRenderer component for the Storyteller web application.
 * Displays an audio streamer with a recording button and progress indicator.
 *
 * @example
 * // Basic usage
 * <AudioStreamerRenderer />
 */

import { StatePropsOfRenderer, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsLabelProps } from '@jsonforms/react'
import { withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MiddlewareContext } from '../../../../MiddlewareContext'
import { StreamingRecorder, StreamingState } from '../../../StreamingRecorder/StreamingRecorder'
import { Instructions } from '../../sections/Instructions'
import { Title } from '../../sections/Title'

/**
 * @function getCaption
 * @description Returns the caption for the audio streamer based on the current state.
 * @param {Record<string, string>} captions - The captions for the audio streamer.
 * @param {StreamingState} state - The current state of the audio streamer.
 * @returns {string} The caption for the audio streamer.
 */
function getCaption(captions: Record<string, string>, state: StreamingState): string {
    switch (state) {
        case StreamingState.IDLE:
        case StreamingState.LOADING:
            return captions.default
        case StreamingState.PLAYING:
            return captions.playing
        case StreamingState.STREAMING:
            return captions.recording
        case StreamingState.PROCESSING:
        default:
            return ''
    }
}

/**
 * @function AudioStreamerRenderer
 * @description Component for displaying an audio streamer with a recording button and progress indicator.
 * @param {StatePropsOfRenderer} props - The props for the AudioStreamerRenderer component.
 * @returns {JSX.Element} The AudioStreamerRenderer component.
 */
function AudioStreamerRenderer(props: StatePropsOfRenderer) {
    const {
        audio,
        captions,
        cta,
        maxRecordingTime,
        recordingStartMode,
        recordingStopMode,
        instructions,
        hideInstructions,
    } = props.uischema.options

    const { t } = useTranslation()
    const { exit, submit: onSubmit, question } = useContext(MiddlewareContext)
    const [state, setState] = useState<StreamingState>(StreamingState.IDLE)
    const params = new URLSearchParams(window.location.search)

    const localisedAudio = {
        file: t(audio.file),
        duration: parseInt(t(`${audio.duration}`)),
    }
    const titles = cta
        ? {
              play: t(cta.play),
              playing: t(cta.playing),
              recording: t(cta.recording),
          }
        : undefined

    /**
     * Submit when streaming is finished
     */
    async function onStreamingEnded(recording: string) {
        const errors = await onSubmit(question.id, { recording })
        if (errors) {
            setState(StreamingState.IDLE)
        }
    }

    /**
     * Change the state of the streaming so UI can be updated based on it
     */
    function onStateChange(state: StreamingState) {
        setState(state)
    }

    return (
        <>
            <Title flexible>{t(getCaption(captions, state))}</Title>
            <StreamingRecorder
                attempt={0}
                skippable={params.get('debug') === 'true'}
                onStreamingEnded={onStreamingEnded}
                onStateChange={onStateChange}
                onExit={exit}
                audio={localisedAudio}
                titles={titles}
                recordingStartMode={recordingStartMode}
                recordingStopMode={recordingStopMode}
                maxRecordingTime={maxRecordingTime}
            />
            {instructions && !hideInstructions.includes(state) && (
                <Instructions instructions={t(instructions)} />
            )}
        </>
    )
}

export const AudioStreamerTester = rankWith(3, uiTypeIs('AudioStreamer'))

export default withVanillaControlProps(withJsonFormsLabelProps(AudioStreamerRenderer))
